import type { PageInfoTranslations } from "../../types/pageInfo";

export const pageInfo: PageInfoTranslations = {
  buttonLabel: "About this page: {page}",
  eyebrow: "About this page",
  showMe: "Show me",
  showMeLabel: "Show me: {task}",
  moreHelp: "More in the Help centre",
  search: {
    label: "Search help on every page",
    placeholder: "Search, e.g. “share a file” or “change a role”",
    clear: "Clear search",
    matchesHeading: "Matches",
    relatedHeading: "Related",
    loadingRelated: "Finding related tasks…",
    relatedUnavailable: "Related tasks could not be loaded. Showing word matches only.",
    noMatches: "Nothing matches. Try other words, or describe what you want to do.",
    otherPage: "On {page}",
    goToPage: "Go to {page}",
    openBuildingFirst: "Open a building to use this page.",
    resultCount: "Results: {count}",
  },
  guide: {
    title: "Help",
    subtitle: "How Franklin works, and what you can do on every page.",
    overviewTitle: "How Franklin is organised",
    overview: [
      "Franklin keeps everything about your buildings in one place. Your organisation has buildings, each building has projects, and each project has its own documents, brief, issues, contacts and signings.",
      "On a building, the switch at the top of the sidebar chooses between Building view, which covers the whole building, and Projects view, where every page follows the project you pick.",
      "Franklin reads the documents you upload to help you work with them: it gives each a category and tags, makes its text searchable, and proposes values for the building's structured data. These are aids, not guarantees — Franklin can miss things or get them wrong, so check what matters to you.",
      "“Ask Franklin…” at the bottom of building pages helps you look for information in the building's documents, data and issues, pointing to the sources it used, and can make changes for you after asking you to confirm. Its answers may be incomplete, so check the sources before relying on them.",
      "What you see depends on your role and project access, and this guide lists only what is open to you. The “i” icon beside any page's title explains that page.",
    ],
    areas: {
      buildings: "Buildings",
      projects: "Projects",
      documents: "Documents",
      issues: "Issues",
      viewers: "Model and drawings",
      account: "Your account",
      organisation: "Organisation",
      billing: "Billing",
    },
    openPage: "Open {page}",
    showTasks: "What you can do ({count})",
  },
  projectLogTitle: "Project documents log",
  pages: {
    buildings: {
      intro:
        "Every building your organisation manages, in one sortable table. Expand a building to see its projects with their document, open-issue and member counts, or open the building to reach its documents, issues and data. Archived buildings are kept apart under “Archived” and can be restored.",
      items: {
        open: {
          title: "Open a building",
          points: [
            "Click a row to open the building.",
            "If you manage the organisation you land on the building dashboard; otherwise on the building's documents.",
            "Click a column header — “Building Name”, “Address”, “Building Registry Code” or “Net Area” — to sort; click it again to reverse the order.",
            "A building whose plan is still awaiting billing confirmation is greyed out and can't be opened yet.",
          ],
        },
        projects: {
          title: "Go straight to a project",
          points: [
            "Click the arrow at the start of a row (“Show projects”) to list the building's projects.",
            "Each project shows its status, a “Default” badge on the default project, and its counts of documents, open issues and members.",
            "Click a project to open its documents in Projects view, scoped to that project.",
            "A building with no projects offers “+ Add project” if you can create projects.",
          ],
        },
        search: {
          title: "Find a building or project",
          points: [
            "Type in “Search buildings and projects...” — it matches building and project names, ignoring case.",
            "A building found through one of its projects opens up by itself, with the matching project highlighted.",
            "In the archived list the search matches building names only.",
          ],
        },
        add: {
          title: "Add a building",
          points: [
            "Click “Add New Building”, or “Create new building” while the list is empty.",
            "Enter the building name and address; pick an address suggestion to fill in the “Building Registry Code” as well.",
            "The registry code is optional, but when given it must be exactly 9 digits.",
            "An organisation owner may also be asked to choose a plan — Free, Starter, Growing or Scale — and confirm with “Agree and add building”.",
          ],
        },
        rename: {
          title: "Rename a building",
          points: [
            "Hover over the name and click the pencil.",
            "Enter or the save button keeps the new name; Esc or the cross cancels.",
          ],
        },
        archive: {
          title: "Archive or restore a building",
          points: [
            "Click the bin icon at the end of a row and confirm with “Archive”.",
            "Choose “Archived” in the sidebar to see archived buildings; it is greyed out while there are none.",
            "Click “Restore” on an archived building and confirm; you may be asked to agree to its cost first (“Agree and restore building”).",
          ],
        },
      },
    },
    building: {
      intro:
        "The building dashboard: the building's key details, its projects, how complete its building data is and how its documents have been arriving. It always covers the whole building — open a project to switch to Projects view, where every page in the sidebar follows that one project. The building's name and address sit in the line above the title on every building page.",
      items: {
        openProject: {
          title: "Open a project",
          points: [
            "Click a project tile to open that project's dashboard; the sidebar switches to Projects view.",
            "Each tile shows the project's description, status and counts of documents, members and issues.",
            "The 12 most recently edited projects are shown; “See all (N) →” shows the rest.",
            "When there are both active and inactive projects, filter the tiles with All, Active or Inactive beside the “Projects” title.",
          ],
        },
        newProject: {
          title: "Create a project",
          points: [
            "Click “New project” beside the “Projects” title.",
            "Enter a “Project name” and, optionally, a description, then click “Create project”.",
            "Make one project per development, construction or renovation project, and split its files into folders inside it.",
          ],
        },
        manageProject: {
          title: "Edit a project or change its status",
          points: [
            "Open a tile's ⋮ menu and choose “Edit” to change the name and description, then click “Update Project”.",
            "Choose “Mark as inactive” or “Mark as active” in the same menu, or click the coloured status dot on the tile.",
            "Inactive is only a label — the project and its files stay as they are.",
          ],
        },
        archiveProject: {
          title: "Delete or restore a project",
          points: [
            "In a tile's “Edit” dialog, click “Delete project” and confirm; the default project can't be deleted.",
            "Nothing is lost: “Show archived (N)” beside the title lists deleted projects.",
            "Click the restore button on an archived tile and confirm with “Restore” to bring it back.",
          ],
        },
        details: {
          title: "Correct the building's details",
          points: [
            "Click “Registry Code”, “Net Area” or “Floors” under the title to edit it in a small popover; Enter saves.",
            "Registry Code and Floors take whole numbers; Net Area takes a decimal, with a comma or a dot.",
            "Hover over Net Area or Floors to see where the value came from: a document (click it to preview), a public registry, or who edited or confirmed it.",
          ],
        },
        buildingData: {
          title: "Check the building data",
          points: [
            "The “Building Data” section shows the share of fields confirmed and a bar of Confirmed, Pending, Empty and Conflicts.",
            "Click a conflict row to open that field, or “… fields to fill” to list the missing ones.",
            "The expand icon (“Open building data”) opens the Building Data page.",
          ],
        },
        analytics: {
          title: "Read the document analytics",
          points: [
            "“Document analytics” appears once the building has documents, with the file total and how many are processed.",
            "“Documents by” shows Category, Discipline and Stage as donuts; “+N more” lists every entry.",
            "“Upload Activity” switches between 30d, 90d and 1y, starting on the shortest range that contains the latest upload; “Documents per Project” appears with two or more projects.",
            "The expand icon on a chart (“Expand chart”) opens it larger.",
          ],
        },
        viewMode: {
          title: "Switch between Building and Projects view",
          points: [
            "Use the Building / Projects switch at the top of the sidebar.",
            "Choosing Projects asks you to pick a project — search the list, or create one with “New project”; a building with only one project opens it directly.",
            "In Projects view, the chevron beside the project name above the title switches project.",
            "Switching back to Building view returns every page to the whole building.",
          ],
        },
        chat: {
          title: "Ask Franklin",
          points: [
            "Click “Ask Franklin…” at the bottom of the page to ask about this building's documents, data and issues.",
            "Franklin helps you find answers and links the documents it used; its answers may be incomplete, so check the sources.",
            "Or just start typing anywhere on the page outside a text box or dialog — your text goes straight into the chat.",
          ],
        },
      },
    },
    buildingData: {
      intro:
        "The building's structured attributes — areas, materials, fire safety and more. Franklin suggests values from the building's documents and public registries, but it can miss a value or read one wrongly, so every value shows its status and where it came from: review the suggested ones, confirm what is right and correct or fill in the rest. The ring at the top shows how much is confirmed, and every change is kept in the history.",
      items: {
        review: {
          title: "Review suggested values",
          points: [
            "Click “Review … fields”, or press R, to step through the values waiting for a decision.",
            "Each one shows the source passage with its page, Franklin's confidence, and any “Other candidates” you can pick instead.",
            "“Accept” keeps the value, “Reject” clears it to Missing; “Skip” and “Previous” move on without deciding.",
            "Keys: A accepts, X rejects, S or → goes to the next, ← to the previous, Esc exits.",
          ],
        },
        inspect: {
          title: "See where a value came from",
          points: [
            "Click a field to open the inspector beside the list; drag its edge to resize it.",
            "It shows the current value, the source evidence and the field's history.",
            "Click a source document to open it; “Other mentions (N)” lists further passages that mention the field.",
          ],
        },
        confirm: {
          title: "Confirm, edit or reject a value",
          points: [
            "On a Needs review row, click the tick to confirm, the pencil to edit or the cross to reject.",
            "While editing, Enter saves and Esc cancels; an unusual value asks you to “Save anyway” or “Edit”.",
            "After a change, click “Undo” in the message to restore the previous value.",
            "A confirmed field keeps only the pencil, for later corrections.",
          ],
        },
        conflicts: {
          title: "Resolve conflicting values",
          points: [
            "A field whose sources disagree shows “… options” instead of a single value.",
            "Click its pencil to see the candidates, then click one to confirm it, or type your own value and save it.",
            "When a new finding differs from a confirmed value, choose “Keep current value” or “Accept new value”.",
          ],
        },
        confirmSection: {
          title: "Confirm a whole section",
          points: [
            "Each section header shows how many of its fields are confirmed; click it to collapse or expand the section.",
            "Click “Confirm all N” in the header to confirm the section's values waiting for review.",
            "Fields with conflicting values are left out, and unusual values are left for you to review one by one.",
          ],
        },
        fillMissing: {
          title: "Fill in a missing value",
          points: [
            "Choose the Missing tab to list the empty fields.",
            "Click “Add value” on a row and enter it; fields with set choices offer a list to pick from.",
          ],
        },
        filter: {
          title: "Filter by status",
          points: [
            "Switch between All, Confirmed, Needs review and Missing; each tab shows its count.",
            "Hover over the ring at the top for the counts behind its percentage.",
          ],
        },
        search: {
          title: "Search fields",
          points: [
            "Type in “Search fields…”, or press / to jump to it.",
            "On a wide screen, the “Sections” list on the left jumps to a section; its button collapses it.",
          ],
        },
        history: {
          title: "See what changed",
          points: [
            "The history icon opens “Building data history”, every change to the building's data.",
            "Click an entry for that field's value history; a field's own history is also in its inspector.",
          ],
        },
      },
    },
    editBuilding: {
      intro:
        "Edit the basic details that identify the building across Franklin: its name, address, year built and net area. Saving takes you back to the building dashboard. The registry code, floors and the other attributes are edited on the building dashboard and the Building Data page instead.",
      items: {
        fields: {
          title: "Change the details",
          points: [
            "Edit “Building Name”, “Address”, “Year Built” and “Net Area”.",
            "The name needs at least 2 characters and the address at least 5.",
            "Year Built is required: a four-digit year no later than this year. Net Area is optional.",
          ],
        },
        address: {
          title: "Pick an address",
          points: [
            "Type at least 3 characters in Address to get suggestions from the Estonian address data system; click one to fill it in.",
            "The address is only saved when you change it.",
          ],
        },
        save: {
          title: "Save",
          points: [
            "Click “Save” to keep the changes and return to the building dashboard.",
            "A field with a problem shows a message under it; nothing is saved until it is fixed.",
          ],
        },
      },
    },
    projectDashboard: {
      intro:
        "The selected project at a glance: its message board, what has changed recently, the issues due for you, the project brief and the project's document analytics. It always covers one project — switch project with the chevron beside its name above the title. Each section links on to the full page: Issues, My Issues, Documents or the Brief.",
      items: {
        messages: {
          title: "Post to the message board",
          points: [
            "Write in “Write a message…” to reach everyone on this project; Enter sends, Shift+Enter starts a new line.",
            "Type @ or click “Mention someone” to notify a person; the “… new” badge counts mentions you haven't read.",
            "Attach a file with the paperclip: “Add from Franklin” or “Upload”.",
            "Click the expand icon (“Expand”) to read the board full size.",
          ],
        },
        replyReact: {
          title: "Reply, react, edit or delete",
          points: [
            "Hover over a message and click “Reply” — your reply quotes it; click a quote to jump to the original.",
            "Add a reaction: 👍 ✅ ❤️ 😄 🎉.",
            "“Edit” and “Delete” appear on your own messages only; a deleted message is removed for everyone.",
          ],
        },
        whatsNew: {
          title: "See what's new",
          points: [
            "Pick the time range in “What's new”: 24 h, 72 h, 7 days or 30 days; your choice is remembered.",
            "Tiles count new, completed and updated issues, comments, new documents and new versions; click an example to open it.",
            "“For you” lists where you were mentioned or assigned, and what's new in your disciplines.",
            "Issue counts need access to issues; document changes are hidden if you can't see all of the project's documents.",
          ],
        },
        myTasks: {
          title: "Your tasks",
          points: [
            "“My tasks” lists open issues assigned to you that are due within the “Due within” window: 1 week, 2 weeks or 1 month.",
            "Overdue issues come first with a red date; ones due in the next 3 days have a blue date.",
            "Icons show linked documents and comments; click an issue to open it in My Issues, or click “View all my tasks”.",
          ],
        },
        brief: {
          title: "The project brief",
          points: [
            "“Franklin's suggestions” on the brief are listed most serious first; click one to open it on the Brief page.",
            "With no suggestions, click “Go to brief”; a project without a brief offers “Start the brief” if you may start one.",
          ],
        },
        analytics: {
          title: "Document activity",
          points: [
            "Charts of this project's documents by Category, Discipline and Stage, and its “Upload Activity” over 30d, 90d or 1y.",
            "“Uploaded today” lists today's uploads, newest first; click one to preview it, or click “View all”.",
            "Hidden while the project has no documents; if you can't see all of its documents, only the title shows, with the reason.",
          ],
        },
        switchProject: {
          title: "Switch to another project",
          points: [
            "Click the chevron beside the project name above the page title (“Switch project”).",
            "Search the list and pick a project; the dashboard and every page in the sidebar follow it.",
            "“New project” at the end of the list creates one and switches to it, if you can create projects.",
          ],
        },
      },
    },
    documents: {
      intro:
        "Every document in this building or project in one place. Franklin reads each file you upload and gives it a category and tags, which you can correct; this helps you find documents by what they say as well as by their name. Browse them as a timeline, a list or in folders, and share, compare, version, comment on or verify them. Filters, search and history sit above the tabs and stay put when you switch view.",
      items: {
        upload: {
          title: "Upload files",
          points: [
            "Click “Upload”, or drop files anywhere on the page.",
            "Pick “Upload Files” or “Upload Folder” — a folder keeps its subfolders.",
            "Check the destination under “Choose where to upload”, or click “Change”.",
            "If a name already exists in that folder, choose “New version”, “Keep both” or “Skip” for each file.",
          ],
        },
        views: {
          title: "Switch view",
          points: [
            "“Folders” shows the project's folder tree; it appears only in Projects view.",
            "“Timeline” places documents by date: set “Date” to “Upload” or “Creation” (the date in the file itself), and zoom between “Years”, “Months”, “Weeks” and “Days”.",
            "“List” is a table; click “Name” or “File modified” to sort.",
            "The tab you pick is remembered next time.",
          ],
        },
        search: {
          title: "Search documents",
          points: [
            "Type in the search box and press Enter — or stop typing for a moment and it searches by itself.",
            "It finds words inside documents as well as in names; a “Content” chip marks text matches, and hovering it shows the page.",
            "In “Folders” it searches the open folder and its subfolders (“Search in folder...”).",
          ],
        },
        filters: {
          title: "Filter by tag or project",
          points: [
            "Click “Filters” and tick values under “Projects”, “Categories”, “Design Disciplines” or “Design Stages”.",
            "Type in “Search tags…” to find a tag without opening each group.",
            "Switch on “Verified only” to see just documents marked “Verified”.",
            "Filters apply to every tab; “Clear all” removes them. Inside a project, the project is fixed.",
          ],
        },
        selection: {
          title: "Work with selected documents",
          points: [
            "Tick documents; Shift-click selects a range, Ctrl-click (⌘ on Mac) adds or removes one.",
            "The bar at the bottom offers “Share”, “Download”, “Ask Franklin” (adds them to the chat) and “Delete”, with more under “More”; “Clear” deselects.",
            "Select exactly two PDFs for “Compare PDFs”, or two processed IFC models for “Compare IFCs”.",
            "Right-click a document for the same actions plus “Rename”, “Add new version” and “Comments”; click a filename to preview it.",
            "On a PDF or signed container, “View signatures” in the row's menu has Dokobit check its digital signatures, including files signed outside Franklin, and shows any that fail the check or that the file has none.",
          ],
        },
        newDocument: {
          title: "Create a document from a template",
          points: [
            "Click “New document” and pick a template, such as “Covered works act” or “Construction diary”.",
            "“Choose pre-filled form” starts from a form already filed; “Import” copies a single field from one.",
            "Choose the “PDF language”; tick “Form will be signed digitally” to sign it straight after saving.",
            "Click “Save”, then pick the project and folder and click “Save here”; the PDF is numbered automatically.",
          ],
        },
        versions: {
          title: "Work with versions",
          points: [
            "A version badge beside a filename unfolds its earlier versions; “View full history” opens “Version History”.",
            "In “Version History”, select two versions to compare them.",
            "Right-click → “Add new version” to upload files or “Choose from documents”; the last one in the list becomes the current version.",
            "An existing document can be added by “Move” (its whole history comes along) or “Copy” (the original stays put).",
          ],
        },
        comments: {
          title: "Comment on a document",
          points: [
            "Right-click a document → “Comments”, or click the comment count on its row.",
            "Write a comment and use @ to mention someone; on a PDF or spreadsheet it is pinned to the page or sheet you are on.",
            "If you can manage issues, “Create issue” is ticked and files the comment as an issue; untick it to keep a plain comment.",
            "“Mark as complete” closes a thread; “Reopen” brings it back.",
          ],
        },
        verify: {
          title: "Mark documents as verified",
          points: [
            "Right-click a document, or select several, and choose “Mark as verified”; “Remove verification” clears it.",
            "Verified documents carry a “Verified” badge; hover it to see who verified it and when.",
            "Click “Undo” in the confirmation to reverse it straight away.",
            "Adding a new version starts that version unverified again.",
          ],
        },
        organise: {
          title: "Organise folders",
          points: [
            "In “Folders”, click “Organise” — it organises the open folder, or the project root if none is open. Right-click a folder → “Organise” works too.",
            "“Franklin decides” reads the files and suggests a structure for you to review; “By tags” groups them by category, discipline or stage instantly.",
            "You can close the window while Franklin works; the button turns into “Review proposal” when it is ready.",
            "Nothing moves until you click “Apply”; new folders can be renamed first.",
          ],
        },
        share: {
          title: "Share links",
          points: [
            "A share link lets anyone with it view and download the files, without a Franklin account.",
            "Select documents and click “Share”, or right-click a folder → “Share Folder”; set “Link expires in” (1 hour to 30 days) and click “Create Link”.",
            "The ⋮ menu → “Manage Shares” lists every link with its views and status, and “Revoke” switches one off at once.",
          ],
        },
        trash: {
          title: "Restore deleted documents",
          points: [
            "“Delete” moves documents to the trash; open it from the ⋮ menu → “Trash”.",
            "Click “Restore” to put a document back.",
            "Deleted documents are kept for 30 days, then removed for good.",
            "If you are allowed to delete files outright, “Delete permanently” and “Empty trash” are there too.",
          ],
        },
        history: {
          title: "See document history",
          points: [
            "The history icon beside the search opens “Document history”: who added, edited, renamed, deleted or restored which document, and when.",
            "Narrow it with “All activity”, “Added”, “Edited”, “Renamed” or “Deleted”, or search by document or person.",
            "Open or download a document from its entry; a deleted one has “Restore”.",
            "Click “Load older” to go further back.",
          ],
        },
      },
    },
    projectFolders: {
      intro:
        "A project's own folders and files, opened from the project's page. The folder tree is on the left and the open folder's contents are on the right. Here you can upload, create folders, search, select, move, share and organise. Franklin reads what you add and tags it to help you find it later, and the same files appear under Documents in the building.",
      items: {
        browse: {
          title: "Browse folders",
          points: [
            "Click a folder in the tree or in the list. The number beside it counts its documents.",
            "The path above the list takes you back up. Click the project name to return to the top.",
            "On a narrow window the tree is hidden, so use the arrow button “Back to project root” instead.",
            "A .zip file opens like a folder, so you can browse what is inside it.",
          ],
        },
        upload: {
          title: "Upload files",
          points: [
            "Click “Upload”, then “Upload Files” or “Upload Folder”. An uploaded folder keeps its subfolders.",
            "Files go into the open folder. Click “Change” in the dialog to pick another folder in this project.",
            "You can also drop files from your computer onto the page, or straight onto a folder in the tree or the path.",
            "If a file with the same name is already there, choose “New version”, “Keep both” or “Skip”.",
          ],
        },
        newFolder: {
          title: "Create a folder",
          points: [
            "Click “New folder” to create one inside the open folder.",
            "Or right-click an empty part of the list → “New folder”.",
            "To create one inside another folder, open that folder's ⋮ menu or right-click it → “Create Subfolder”.",
          ],
        },
        search: {
          title: "Search this folder",
          points: [
            "Type in “Search in folder...” and press Enter, or pause typing.",
            "It searches the open folder and every folder inside it. At the top level that is the whole project.",
            "It matches file names and the text inside documents. A “Content” tag marks text matches.",
          ],
        },
        selection: {
          title: "Work with selected files",
          points: [
            "Tick a file or click its row to select it. Shift-click selects a range and Ctrl/⌘-click adds one more. Click a file name to preview it.",
            "Ticking a folder selects it and everything inside it.",
            "The action bar offers “Share”, “Download”, “Move/Copy”, “Rename”, “Add new version”, “Ask Franklin” and “Delete”, plus “Compare PDFs”, “Signature” or “Mark as verified” where they apply.",
            "Right-click a file for the same actions.",
          ],
        },
        folderMenu: {
          title: "Use a folder's menu",
          points: [
            "Hover over a folder and click ⋮, or right-click it, in the tree or the list.",
            "“Export Folder” downloads it as a zip that keeps its subfolders. “Share Folder” makes a share link for it.",
            "“Create Subfolder”, “Organise”, “Rename” and “Delete” appear when you are allowed to use them.",
            "Deleting a folder moves its documents to the Trash.",
          ],
        },
        move: {
          title: "Move or copy files",
          points: [
            "Drag files or folders onto a folder in the tree, a folder in the list, or a step in the path. Folders that accept the drop are highlighted.",
            "Dragging a ticked item moves the whole selection with it.",
            "To move to another project, or to copy, select the items and click “Move/Copy”. Choose “Move” or “Copy”, then the project and folder.",
          ],
        },
        share: {
          title: "Share a link",
          points: [
            "Select files or folders and click “Share”, or use “Share Folder” on a folder.",
            "Pick when the link expires (“1 hour”, “24 hours”, “7 days” or “30 days”), then click “Create Link” and “Copy Link”.",
            "Anyone with the link can view and download the shared documents without an account.",
          ],
        },
        versions: {
          title: "See and add versions",
          points: [
            "Click the version badge beside a file name to list its earlier versions under it. “View full history” opens them all.",
            "Right-click a file → “Add new version” to add an upload or an existing document as its next version.",
            "Older IFC and DWG versions can be ticked, so you can compare two versions of one file.",
          ],
        },
        organise: {
          title: "Organise folders",
          points: [
            "Click “Organise” to organise the open folder, or the project's top level when no folder is open.",
            "“Franklin decides” suggests a structure from the files' contents for you to review. “By tags” groups them by their tags at once.",
            "Choose “Loose files only” or “Whole folder tree”. The analysis can take a few minutes, and the button changes to “Review proposal” when it is ready.",
            "Nothing moves until you click “Apply”. “Discard” drops the proposal.",
          ],
        },
        specialFolders: {
          title: "Know the special folders",
          points: [
            "“Issue Attachments” holds files attached to issues. They are added from the issues page, so you cannot upload files or create folders in it. Its files can be copied elsewhere but not moved or deleted; remove a file from its issue first.",
            "The “Brief” folder holds the project brief and cannot be renamed, deleted or organised. “Show older versions” lists its confirmed versions, and each one opens on the Brief page. Its Additions.pdf stays in the folder: copy it to use it elsewhere.",
            "A signed container (.asice, .bdoc) is a single file, not a folder. Open it to see its signatures.",
          ],
        },
        trash: {
          title: "Restore deleted files",
          points: [
            "Click ⋮ → “Trash” to see deleted documents. “Back to Documents” returns to the folders.",
            "Click “Restore” on a document to put it back.",
            "Deleted documents are kept for 30 days and then removed for good.",
            "Users with delete rights can also “Delete permanently” or “Empty trash”.",
          ],
        },
      },
    },
    projectLog: {
      intro:
        "A read-only list of the documents in this project, most recently changed first. It includes documents moved to the trash, so you can see everything that has happened here. Use the project's folders page to open, move or restore files.",
      items: {
        read: {
          title: "Read a log entry",
          points: [
            "Each row shows the file name, its version (v1, v2 …), its processing state and when it was last changed.",
            "The last part (#…) is the user number of whoever last changed it.",
            "Every version is its own row, so a file with three versions appears three times.",
          ],
        },
        live: {
          title: "Watch it update",
          points: [
            "The log refreshes by itself when documents are added, changed or moved out of the project.",
            "It shows the 200 most recently changed documents.",
          ],
        },
        archived: {
          title: "Spot deleted documents",
          points: [
            "Documents moved to the trash stay in the log, marked “archived”.",
            "To restore one, open the project's folders and go to ⋮ → “Trash”.",
          ],
        },
      },
    },
    signing: {
      intro:
        "Every signed container and signing chain in this project, newest first. Pick a row to see who has signed, whose turn it is and which documents it covers, then sign, download or check on it from the panel beside the list. Completed signings are filed back with the project's documents. To start a new signing, go to Documents, select files and click “Signature”.",
      items: {
        signTurn: {
          title: "Sign when it's your turn",
          points: [
            "Rows marked “Waiting for you” need your signature; you're emailed when your turn comes.",
            "Select the row and click “Sign now”.",
            "In the signing window choose Smart-ID, Mobile-ID or ID-card and complete signing.",
            "Once you've signed, it moves to the next signer; after the last signature the signed file is filed with your documents.",
          ],
        },
        tabs: {
          title: "Yours or everyone's",
          points: [
            "“All signatures” (the default) lists every signing in this project you're allowed to see.",
            "“My signatures” keeps only the ones you started or were asked to sign.",
          ],
        },
        details: {
          title: "See signers and files",
          points: [
            "Click a row (or press Enter on it) to open it in the side panel; the first row is shown until you pick one.",
            "“Signers” shows progress such as “2 of 3 signed” and each person's state: “Waiting”, “Signed”, “Declined” or “Removed”, plus the signing method used.",
            "Steps are numbered (“Step 1”…); people sharing a step can sign in any order.",
            "“Documents in this session” or “Container contents” lists the files included.",
          ],
        },
        statuses: {
          title: "Read the status",
          points: [
            "“Waiting for you”: your signature is next. “Waiting for others”: someone else's turn.",
            "“Finalising”: every signature is in and the signed container is being sealed; the page keeps checking and turns it “Signed” by itself.",
            "“Failed”, “Cancelled” and “Expired” mean the signing ended without a container; a failed one shows the provider's reason in the panel.",
          ],
        },
        filter: {
          title: "Filter the list",
          points: [
            "Click “Filter”, then choose “Status”, “Signed by” or “Date”.",
            "“Date” takes a “From” and “To” day and matches when the signing was created.",
            "Each active filter shows as a chip; click it to change it, its × to remove it, or “Clear” to remove all.",
          ],
        },
        search: {
          title: "Search signings",
          points: [
            "Type in “Search documents…”; the list narrows as you type.",
            "It matches the document name, the sender and every signer's name.",
          ],
        },
        sort: {
          title: "Sort the list",
          points: [
            "Click the “Document”, “Sender” or “Created” column heading to sort; click again to reverse.",
            "The list starts newest first by “Created”.",
            "“Created” is when the item was created in Franklin, not a date from the file itself.",
          ],
        },
        download: {
          title: "Download a signed container",
          points: [
            "Select a signed container and click “Download” in the panel.",
            "The panel also lists its signatures and its “Container contents”.",
          ],
        },
        selection: {
          title: "Work with several containers",
          points: [
            "Tick signed containers (only they have a tickbox), or tick the header box to select all.",
            "The bar at the bottom offers “Share”, “Download” and “Ask Franklin”, which attaches them to the chat.",
            "If you can edit files in the project, “Mark as verified” or “Remove verification” is there too.",
          ],
        },
        status: {
          title: "Check or cancel a chain",
          points: [
            "“Check status” asks the signing provider where an open chain stands and refreshes it.",
            "The person who sent a chain can click “Cancel signing” while it is still open or finalising.",
            "Cancelling asks you to confirm: everyone still waiting loses access and no container is produced; “Keep it open” backs out.",
          ],
        },
        start: {
          title: "Start a new signing",
          points: [
            "Signings start on Documents: select files from one project and click “Signature”.",
            "Choose “Sign myself” to sign straight away, or “Send to others” to invite people step by step.",
            "Under “Output format”, “ASiC-E container (.asice)” keeps one or more files together in a signed container; “Signed PDF / PAdES (.pdf)” keeps a single PDF as a PDF with the signatures embedded.",
            "A signing holds at most 10 people, you included.",
          ],
        },
        unfiled: {
          title: "Signings without a project",
          points: [
            "A row marked “No project” isn't filed under a project, so it appears in every project of this building.",
            "Only the 500 most recent items are loaded; a note above the list says when older ones are left out.",
          ],
        },
      },
    },
    brief: {
      intro:
        "The project's brief: the client's requirement documents in the Brief folder, plus written additions that change or clarify them. Franklin's suggestions sit beside them: possible gaps and conflicts it has spotted between the brief and the project's documents, issues and building data, some with an issue ready to raise. They help your review rather than replace it — Franklin will not catch everything. Confirm the brief as a numbered version once it is agreed, and later changes are marked and can be compared against that version. Each project has its own brief, and its files also appear under Documents.",
      items: {
        start: {
          title: "Start the brief",
          points: [
            "On a project with no brief, click “Start the brief”: it creates the brief and its folder.",
            "Sending the first addition also starts the brief.",
            "People who cannot manage the brief see “This project has no brief yet.”",
          ],
        },
        addFiles: {
          title: "Add files",
          points: [
            "Click “Add files”, then “Upload” for new files or “Add existing” for documents already in the project.",
            "“Add existing” copies the files you pick into the brief with “Copy into the brief”; the originals stay where they are.",
            "Files in the brief you are not allowed to see are counted above the list, not shown.",
          ],
        },
        additions: {
          title: "Write an addition",
          points: [
            "Type a change or clarification in “Write an addition…” under Additions and click “Send”.",
            "“Attach” adds documents, offering the brief's own files first under “In the brief”.",
            "Right-click a file in the brief (or select several) and choose “New addition” to start one with those files attached.",
            "Additions are listed oldest first; they notify nobody, so @ is plain text.",
          ],
        },
        check: {
          title: "Let Franklin help check the brief",
          points: [
            "Click “Run check now” in Franklin's suggestions; it is unavailable while a check is queued or running.",
            "Franklin also checks by itself after an upload, a change to the brief, an issue change or a building data change; the panel shows why and when (“Last check …”).",
            "A queued check can be stopped with “Cancel check”; a failed one shows “The last check failed” with the reason on hover.",
            "On another page, “Franklin finished checking the brief” pops up with “Review” to come back.",
            "A check helps you spot possible problems; it does not prove the brief is complete or free of conflicts.",
          ],
        },
        suggestions: {
          title: "Read Franklin's suggestions",
          points: [
            "Each suggestion has a severity dot (High, Medium, Low, Info); click it to unfold “What Franklin found”, “In the brief” and “Evidence”.",
            "Click a citation to open the cited document at that page, sheet or model element.",
            "Treat each suggestion as a pointer: open its evidence and judge it yourself.",
            "Point at a suggestion to outline the files it cites in the document list.",
            "“Minimise” shrinks the panel to Franklin's logo, which counts open suggestions and turns while a check runs.",
          ],
        },
        resolveSuggestion: {
          title: "Accept or dismiss a suggestion",
          points: [
            "“Add to brief” opens the suggestion for editing; “Add to the brief” saves it as an addition shown as “Franklin · confirmed by” you.",
            "“Dismiss suggestion” sets it aside; a dismissed one can be brought back with “Reopen”.",
            "Settled suggestions move under “Resolved”, including those “Resolved by a brief update”.",
          ],
        },
        suggestedIssue: {
          title: "Raise a suggested issue",
          points: [
            "Some suggestions carry a “Suggested issue” with “Why”, “Disciplines” and “Suggested assignees” taken from the Contacts page.",
            "Click “Create this issue” to open the issue form filled in from the suggestion.",
            "Once created, the suggestion links to it as “Issue #…”.",
          ],
        },
        additionThread: {
          title: "Reply to, edit or file an addition",
          points: [
            "Under an addition, “Reply” starts a thread, and “Edit” changes its text and attachments.",
            "“Delete” removes it with its replies; confirmed versions keep their copy.",
            "“Create issue” raises an issue from it, then links it as “Issue #…”.",
          ],
        },
        confirm: {
          title: "Confirm a version",
          points: [
            "“Confirm version …” appears once there is something to record: files or additions for the first version, unconfirmed changes after that.",
            "The dialog lists what changed since the last version; add a “Change note” with what changed and why.",
            "Leaving the page after changing the brief asks “Confirm version … before you leave?” — your changes are saved either way.",
          ],
        },
        versions: {
          title: "View or compare past versions",
          points: [
            "Version tabs run oldest to newest, with “Current” last; point at a tab to see who confirmed it, its file count and note.",
            "“Current” is tinted yellow and counts unconfirmed changes; chips such as “New”, “Edited” and “Removed: …” mark them in the lists.",
            "Clicking a version shows it read-only; “Back to current” returns.",
            "The compare icon opens “Compare versions” with “From” and “To”; tick “Show unchanged” to see everything.",
          ],
        },
        history: {
          title: "Brief history",
          points: [
            "“Brief history” lists every confirmed and deleted version with who, when and the change note.",
            "Click a confirmed version there to view it.",
          ],
        },
        deleteVersion: {
          title: "Delete a version",
          points: [
            "While viewing a version, click “Delete version …”, or use the delete action beside it in “Brief history”.",
            "Only the record of that version goes; files and additions are not changed.",
            "The deletion stays in the history, and its number is never used again.",
          ],
        },
      },
    },
    contacts: {
      intro:
        "Who works on this building's projects, how to reach them and what each person is responsible for. Project members appear automatically, and anyone else can be added as an external contact. In Projects view you see one project's contacts; in Building view each project has its own section, with organisation owners and admins listed once under “All projects”. Responsibilities also feed the issue pages — the “My disciplines” filter and suggested assignees.",
      items: {
        add: {
          title: "Add a contact",
          points: [
            "Click “Add contact” at the top, or on a project's section in Building view.",
            "Enter the name, “Position”, “Company”, responsibilities and notes; in Building view, choose the project first.",
            "Add up to 10 phone numbers and 10 email addresses, each with an optional label such as Mobile.",
          ],
        },
        memberDetails: {
          title: "Add details for a project member",
          points: [
            "Members are listed with their account email even before anyone adds details.",
            "Click the + (“Add details”) on their row to add phone numbers, position, responsibilities and notes; it appears on rows you may edit.",
            "A member's name comes from their Franklin profile and can't be changed here.",
          ],
        },
        search: {
          title: "Search",
          points: [
            "Type in “Search contacts…” — it matches names, positions, companies, emails, phone numbers, labels and notes.",
            "Phone numbers match on digits alone, so 5551234 finds +372 555 1234.",
          ],
        },
        filter: {
          title: "Filter by responsibility",
          points: [
            "Click “Responsibilities” and tick one or more disciplines; anyone responsible for any of them stays.",
            "“Clear” removes the filter.",
            "While you search or filter in Building view, projects with no match are hidden.",
          ],
        },
        edit: {
          title: "Edit or delete a contact",
          points: [
            "Open the ⋯ menu on a row and choose “Edit” or “Delete”; it appears only on entries you may change.",
            "Deleting a member's entry removes only the details entered for them — their access is not affected.",
          ],
        },
        copy: {
          title: "Call, email or copy",
          points: [
            "Click a phone number to call it or an email address to write to it.",
            "Hover over a value and click the copy icon to copy it.",
          ],
        },
        badges: {
          title: "Read the labels on a row",
          points: [
            "“External” marks someone without a Franklin account; “You” marks your own row.",
            "“No longer has access” marks a person who has lost access; their row is greyed out.",
            "“Added by …” under the notes shows who entered the details.",
          ],
        },
        sections: {
          title: "Browse projects in Building view",
          points: [
            "Each project is a section showing “Contacts: N”; click its name to collapse or expand it.",
            "Organisation owners and admins are listed once, under “All projects”, rather than in every project.",
          ],
        },
      },
    },
    model: {
      intro:
        "View the building's BIM (IFC) models in 3D. Load one or several models, inspect any element's properties, cut sections, filter by floor, discipline or class, and compare two versions to see what was added, removed or modified. Issues can be attached to elements together with a saved view, and an issue's linked element opens back here from the issue panel on the Issues page. Models come from .ifc files uploaded on Documents.",
      items: {
        models: {
          title: "Choose models",
          points: [
            "Open “Select models” (top right), tick one or more files and click “Apply”; several models load together in one scene.",
            "On a file with earlier versions, click its version pill (“v2 ⌄”) to list them and pick one instead.",
            "The eye icon (“Hide in 3D view” / “Show in 3D view”) hides a loaded model without unloading it.",
            "With more than 10 files, “Browse all … IFC files” opens a full browser; with no models yet, “Go to Documents” takes you to upload an .ifc file.",
          ],
        },
        inspect: {
          title: "Inspect an element",
          points: [
            "Click an element in 3D to open “Properties”: type, name, GUID, elevation, every property and its materials with their layers.",
            "“Highlight similar elements” lights up every element of the same IFC class; click it again to turn it off.",
            "Right-click an element for the same action and more.",
          ],
        },
        spatialTree: {
          title: "Browse the spatial structure",
          points: [
            "Click “Spatial structure” to see each model's building, storeys and element classes as a tree.",
            "Click a storey to highlight everything in it and frame the camera on it.",
            "Click a class to highlight all its elements on that storey, or an element to select it; the arrow expands a row.",
          ],
        },
        section: {
          title: "Cut a section",
          points: [
            "Click “Section box (elevation & section cuts)”, then drag the box's arrows or the sliders, or type bounds in metres, for “Elevation (height)”, “Section (width)” and “Section (depth)”.",
            "“Isolate storey” cuts the box to one storey; “Whole building” restores it.",
            "Right-click an element → “Section box around this”, or right-click a surface → “Cut here” for an angled cut, listed under “Oblique cuts”.",
            "Opening the panel cuts nothing until you ask; the red X on the button clears the cut.",
          ],
        },
        sectionOptions: {
          title: "Adjust a section",
          points: [
            "“Suspend cut” shows the whole model and keeps the cut; “Resume cut” brings it back.",
            "“Hide handles” removes the arrows; “Snap to floors” snaps the height cut to storey levels.",
            "“Top view”, “Side view” and “Front view” point the camera along each cut.",
            "“Reset box” returns to the full model; “Clear section” removes the cut; hiding the panel keeps the cut.",
          ],
        },
        filters: {
          title: "Filter the model",
          points: [
            "Click “Filters” and choose by “Floor”, “Discipline” or “Class”; tick “Show spaces” to show rooms.",
            "“Clear” in the panel, or the red X on the button, removes every filter.",
            "While comparing, the same filters narrow the comparison.",
          ],
        },
        issueFromElement: {
          title: "Create an issue on an element",
          points: [
            "Select an element and click “Create issue from this element” under “Issues for this element”, or right-click → “Create issue”.",
            "The issue is linked to the element and saves the current view: models, camera and section cut.",
            "It is filed in the model's project; if you have no issue rights there, you are asked to request access.",
          ],
        },
        modelIssues: {
          title: "Issues on the model",
          points: [
            "“Issues attached to the model” lists every issue linked to the loaded models, with number, assignees and status; “Issues for this element” shows only the selected element's issues.",
            "“Expand issue” opens the issue here without leaving the viewer.",
            "“Restore saved view” brings back the models, camera and cut saved with the issue; “Go to element” frames its element.",
          ],
        },
        compare: {
          title: "Compare two versions",
          points: [
            "In “Select models”, tick exactly two and click “Compare differences”; two versions of one file can only be compared, not loaded together.",
            "At the top, “Older” and “Newer” choose which model is shown; “Change file” switches either side to another file or version.",
            "“Swap older / newer” flips them; “Exit comparison” returns to normal viewing.",
            "The address keeps the pair, so the link reopens the same comparison.",
          ],
        },
        compareChanges: {
          title: "Review the changes",
          points: [
            "The “Changes” panel lists elements “Added” (green), “Removed” (red) and “Modified” (amber); unchanged ones are grey.",
            "“Group by” Class, Storey or Discipline, or use “Search elements…”.",
            "Click a change to see “Before” and “After” for its properties, materials and attributes.",
            "“Isolate changes” hides unchanged elements; openings and other bodyless elements count as changes but cannot be drawn in 3D.",
          ],
        },
        navigate: {
          title: "Move around",
          points: [
            "Drag to rotate, scroll to zoom.",
            "After clicking the view, W A S D or the arrow keys move the camera.",
            "The navigation buttons offer “Bird's-eye view”, “Side view (cycles through the 4 sides)” and “Fit to model”.",
            "“Hide navigation” and “Hide controls” fold the buttons away.",
          ],
        },
        screenshot: {
          title: "Save a screenshot",
          points: [
            "The camera icon (“Download screenshot”) saves the current view as a PNG.",
            "Right-click anywhere in the model → “Take screenshot” does the same.",
          ],
        },
      },
    },
    drawings: {
      intro:
        "View the project's DWG drawings in the browser, in their original colours. Open one drawing to read it, or two — typically two revisions — to see what was added and removed. You can measure, hide layers, export to PDF and pin issues to the spot they concern; those issues are ordinary project issues and also appear on the Issues page. What you see is a visual preview, so check the source DWG for detailed review.",
      items: {
        open: {
          title: "Open a drawing",
          points: [
            "Click “Choose drawing…”, tick one drawing and click “Apply”.",
            "Older revisions sit under the current one: click its version badge to “Show older versions”.",
            "With more than 10 drawings the list is grouped into “Selected”, “Recent” and “Latest uploads”, and “Browse all … DWG files” opens the full folder view.",
            "From Documents, select one DWG and click “View drawing”. The drawings you open stay in the page address, so a reload, Back or a shared link opens the same view.",
          ],
        },
        compare: {
          title: "Compare two drawings",
          points: [
            "In “Choose drawing…”, tick two drawings and click “Compare differences”. A third tick is refused — the limit is two.",
            "Franklin decides which is older: by version number within one file, otherwise by upload date. The picker marks them “Older” and “Newer”.",
            "“Swap older / newer” reverses them; “Stop comparing” goes back to the older drawing alone.",
            "From Documents, select two DWGs and click “Compare drawings”.",
          ],
        },
        modes: {
          title: "Choose how changes are shown",
          points: [
            "While comparing, pick a mode in the pill at the top: “Overlay”, “Changes only”, “Swipe”, “Blink” or “Side by side”.",
            "“Swipe”: drag the red divider across the drawing to wipe between the two.",
            "“Blink”: the changes flash on and off by themselves, about every 0.65 seconds.",
            "“Side by side” shows the two drawings next to each other.",
          ],
        },
        legend: {
          title: "Read what changed",
          points: [
            "While comparing, the legend under the toolbar counts what was “Added” (green) and “Removed” (red); “Unchanged” lines are grey.",
            "When there are any, “Redrawn rows” counts entities deleted and redrawn in exactly the same place and style. They change nothing on the page, so they are left out of the counts — hover or click it for details.",
            "Opening “Layers” replaces the legend until you close it.",
          ],
        },
        navigate: {
          title: "Move around the drawing",
          points: [
            "Drag to pan; scroll to zoom towards the cursor, or use “Zoom in” and “Zoom out”.",
            "“Fit” returns to the view the file was saved with, or the whole drawing — both drawings when comparing.",
            "The sun/moon button switches between a “Light” and a “Dark” background.",
            "“Hide controls” folds the toolbar away; “Show controls” brings it back.",
          ],
        },
        measure: {
          title: "Measure",
          points: [
            "“Ruler”: click two points to read the distance.",
            "“Area”: click three or more corners to read the area in m².",
            "“Coordinate”: click a point to read its X and Y in mm.",
            "The result shows at the bottom of the drawing. Esc clears it; dragging still pans while a tool is on, and clicking the button again turns it off.",
          ],
        },
        layers: {
          title: "Show or hide layers",
          points: [
            "Click “Layers” and untick a layer to hide it; each row shows the layer's colour.",
            "“All” shows every layer again.",
            "When comparing, a blue dot marks layers with changes, and “Changed only” shows just those.",
          ],
        },
        issues: {
          title: "Pin an issue on the drawing",
          points: [
            "Click “Create issue”, then click the spot it belongs to — or right-click the spot and choose “Create issue”. Esc cancels.",
            "The issue form opens with the drawing attached (both, when comparing) and the spot saved.",
            "The issue is filed in the drawing's project and grouped under that drawing's issue on the Issues page.",
          ],
        },
        issueMarkers: {
          title: "See issues on the drawing",
          points: [
            "Issues pinned to the open drawings show as numbered bubbles, coloured by status; in “Side by side” they appear on both halves.",
            "Click a bubble to open the issue and edit it without leaving the drawing.",
            "“Hide issues” clears the bubbles; “Show issues” brings them back.",
          ],
        },
        export: {
          title: "Export a PDF or screenshot",
          points: [
            "“Export PDF” opens an A3 preview of the whole drawing.",
            "“Draw a window”, then click two corners, to export just that area; “Whole drawing” goes back to all of it.",
            "Click “Download PDF” to save it, or “Cancel”.",
            "“Download screenshot” saves the current view as a PNG image.",
          ],
        },
      },
    },
    issues: {
      intro:
        "Every issue in the project — tasks, defects and questions — as a list, a kanban board or a Gantt schedule. Open one to edit its fields, discuss it in comments, attach documents and tick off a checklist. Issues raised on a drawing, a model element or a document comment land here too, and “Create protocol” turns a period's activity into a PDF. My Issues shows only the ones that concern you; Notifications shows what changed.",
      items: {
        newIssue: {
          title: "Create an issue",
          points: [
            "Click “New Issue”: a draft opens in the side panel and appears in the list at once. Write the “Description” and set the status, assignees, dates and discipline.",
            "The issue is filed in the project you are working in; there is no project to choose.",
            "Click “Create”, press Enter outside a text field, or just leave the draft: it is saved once it has a description, and an empty draft is discarded.",
            "Under the description, “Mark as subissue” picks a parent and “Add checklist” lists items before you save; “Add from Franklin” or “Upload” attaches documents.",
            "People responsible for the chosen discipline are listed first, under “Responsible for these disciplines”.",
            "In Kanban, “+ Add Issue” under a column presets its status; in Gantt, “Add issue here” on a group row presets that group. Issues raised from a model, a drawing or the brief open the same fields in a dialog.",
          ],
        },
        open: {
          title: "Open and edit an issue",
          points: [
            "Click an issue to open its panel; click it again, or press Esc, to close it. Drag the panel's left edge to resize it.",
            "Click the title to rename it, and change status, assignees, dates and “Tags” in place. Only people who can manage issues in that project can edit.",
            "The “+” menu adds a checklist or a subissue, or makes this issue a subissue; the clock icon opens “Issue history”.",
            "Linked model elements are listed under “Elements”; click one to open the 3D model at that element.",
          ],
        },
        views: {
          title: "List, Kanban or Gantt",
          points: [
            "List: grouped rows with status, discipline, assignees, due date, comment and document counts; edit the cells in place.",
            "Kanban: drag cards between status columns or up and down within one.",
            "Gantt: drag a bar to move it or its ends to change its dates, and switch the scale between Day, Week and Month.",
            "Gantt changes wait in “unsaved changes” until you click “Confirm” or “Discard”; leaving the page asks first.",
          ],
        },
        filters: {
          title: "Filter, sort and search",
          points: [
            "“Filters” narrows by status, “Overdue only”, assignee, discipline, “Has linked documents” or “Has comments”; active filters show as chips beside it.",
            "“Sort” groups the List and Gantt by creation date, discipline, status, assignee or due date.",
            "The search box matches issue titles.",
            "“My disciplines” appears when you have responsibilities on the Contacts page and filters to them.",
          ],
        },
        bulkEdit: {
          title: "Right-click and bulk edit",
          points: [
            "Right-click an issue to change its status, assignees, discipline or dates, “Move to project”, “Create subissue…” or “Add document”.",
            "In the List, tick the box at the start of a row; Shift-click ticks a range.",
            "“Actions” in the bar that appears applies status, assignees, discipline, dates or a project move to every ticked issue.",
            "“Ask Franklin” attaches the issues to the chat so you can ask about them.",
          ],
        },
        comments: {
          title: "Comment and mention",
          points: [
            "Write in “Add a comment…” and press Enter or “Send”; you can attach documents to a comment.",
            "Type @ or use “Mention someone” to notify a project member.",
            "“Reply” quotes a comment and mentions its author; “Edit” changes your own comments.",
          ],
        },
        attachments: {
          title: "Attach documents",
          points: [
            "In the issue's “Documents” section, “Attach” → “Add from Franklin” links an existing file, or “Upload” adds new ones.",
            "Uploaded files are filed in the project's “Issue Attachments” folder.",
            "Images and PDFs show as a gallery; click one to open it, or click “Documents” to fold the section away.",
          ],
        },
        statuses: {
          title: "Statuses",
          points: [
            "Every project has In Progress, Resolved and Closed; change a status from the row, the panel or by dragging a kanban card.",
            "With one project chosen, add your own with “New status” — in a status picker, in “Filters” or as the board's last column.",
            "Hover a custom status in a picker to edit or delete it; one still in use cannot be deleted. Drag a custom column's handle to reorder the board.",
          ],
        },
        checklists: {
          title: "Checklists",
          points: [
            "In an open issue, click “+” → “Add checklist”, or right-click the issue → “Add checklist”.",
            "Type in “Add an item”, tick items off, click one to rename it and drag to reorder.",
            "Progress such as 2/5 shows on the issue's row and kanban card.",
          ],
        },
        dependencies: {
          title: "Dependencies",
          points: [
            "Under “Dependencies” in an open issue, add what it is “Waiting on” and what it “Drives”; both issues must be in the same project.",
            "Choose the relationship (such as “Finish → start”), a lag in days, and “Rigid” (moves with its predecessor) or “Flexible” (only warns).",
            "In Gantt, drag from a bar's end dot onto another bar to link them; Esc cancels.",
            "When a change moves dependent issues, choose “Update schedule” or “Keep current dates”; “Undo” on the notice puts it back.",
          ],
        },
        snapshots: {
          title: "Schedule snapshots",
          points: [
            "With one project chosen, open Gantt and click the bookmark icon beside the scale to “Save snapshot”; confirm or discard unsaved changes first.",
            "The clock icon beside it opens “Snapshot history”; pick one to view it read-only, “Rename” it or delete it.",
            "“Compare with today” shows what moved, was added or is gone since; “Back to the live schedule” leaves the snapshot.",
          ],
        },
        protocol: {
          title: "Create a protocol",
          points: [
            "Click “Create protocol” and pick the period: Today, This week, This month or a custom range.",
            "Untick issues that should not appear, then tick what the document prints — summary, issue table, activity log — and add your own sections.",
            "Under “Attachments”, “Attach” adds files from Franklin or your computer; tick “Attachments” among the issue options to include the selected issues' files too. Images are printed and PDFs appended page by page; other files are listed by name. Files from your computer go only into the PDF, not into the project.",
            "“Design” picks the look — “Classic”, “Modern” or “Compact”; the content stays the same.",
            "Choose the PDF language, check the “Preview” and click “Export PDF”.",
            "The PDF downloads; “Save to project” also files it in a project folder.",
          ],
        },
      },
    },
    myIssues: {
      intro:
        "Your personal slice of the building's issues: the ones assigned to you, created by you or mentioning you, plus your overdue work on its own tab. It follows the project chosen in the sidebar and uses the same list, board, detail panel and menus as the Issues page. The red dot on “My Issues” in the sidebar means someone has @mentioned you in a comment you have not read yet.",
      items: {
        tabs: {
          title: "Switch between Assigned, Created, Mentions and Overdue",
          points: [
            "“Assigned”: issues where you are an assignee. “Created”: issues you created. “Mentions”: issues where you are @mentioned in a comment.",
            "“Overdue”: issues assigned to you that are in progress and past their due date. Its red count shows on every tab.",
            "The tab decides whose issues you see, so the filters offer no assignee choice here.",
          ],
        },
        open: {
          title: "Open an issue",
          points: [
            "Click an issue to open its detail panel over the list; click it again to close it.",
            "Comment in “Add a comment…” and type @ to mention someone.",
            "Opening an issue marks your unread mentions on it as read. Until then a red dot sits on its comment count.",
          ],
        },
        newIssue: {
          title: "Create an issue",
          points: [
            "Click “New Issue”. The issue is filed in the project you are working in; there is no project to choose.",
            "On the Kanban board, “Add Issue” under a column opens the form with that column's status already set.",
          ],
        },
        layout: {
          title: "Choose List or Kanban",
          points: [
            "“List” shows rows with columns; click a column heading to sort by it.",
            "“Kanban” shows a card per issue in status columns; drag a card to change its status.",
            "The “Overdue” tab is always a list.",
          ],
        },
        filters: {
          title: "Filter and search",
          points: [
            "“Filters” narrows by status, discipline, linked documents or comments; each active filter shows as a removable chip.",
            "Type in “Search issues…” to match the issue text.",
            "When filters hide some of your issues, “… issues hidden by filters” appears with “Clear Filters”.",
          ],
        },
        myDisciplines: {
          title: "Show your disciplines only",
          points: [
            "“My disciplines” sets the discipline filter to your responsibilities in this project; click it again to clear it.",
            "The button appears only once your responsibilities are set on the Contacts page.",
          ],
        },
        sort: {
          title: "Sort and group the list",
          points: [
            "“Sort” groups the list by “Creation Date”, “Discipline”, “Status”, “Assignee” or “Due Date”, with a heading per group.",
            "It is offered in the list only; the Kanban columns already group by status.",
          ],
        },
        quickEdit: {
          title: "Edit from the row or card",
          points: [
            "Click a row's status, discipline, assignees or due date to change it in place.",
            "Right-click an issue for “Change description”, “Status”, “Assignees”, “Start date”, “Due date”, “Move to project”, “Create subissue…”, “Add checklist” and “Ask Franklin”.",
          ],
        },
        bulk: {
          title: "Change several issues at once",
          points: [
            "In the list, tick the boxes beside issues; Shift-click selects every issue between two ticks.",
            "Open “Actions” in the bar that appears to set status, assignees, discipline, dates or project for all of them, or “Ask Franklin” about them.",
          ],
        },
      },
    },
    notifications: {
      intro:
        "A feed of everything that has happened on the issues you can access in this building, newest first and grouped by day: issues created or deleted, and changes to titles, statuses, dates, assignees, files, BIM elements, comments, checklists and dependencies. It follows the project chosen in the sidebar. Opening the page marks the feed as seen and clears the red dot on “Notifications” in the sidebar.",
      items: {
        open: {
          title: "Go to the issue",
          points: [
            "Each entry shows who did what, the issue's number and title, and the date and time.",
            "Click an entry to open that issue on the Issues page.",
          ],
        },
        filter: {
          title: "Filter the feed",
          points: [
            "Click “Filters” and pick “Activity type”, “Person”, “Unread” or “Time range”.",
            "“Activity type” covers kinds such as “Status”, “Dates”, “Comments”, “BIM elements” and “Checklist”; “Person” is who made the change.",
            "“Time range” offers “Today”, “Last 7 days” and “Last 30 days”. Each active filter shows as a removable chip.",
          ],
        },
        unread: {
          title: "See what is new",
          points: [
            "The red dot on “Notifications” in the sidebar lights up for activity by others since you last opened this page.",
            "“Unread only” keeps just that activity. Opening the page counts as a visit, so it shows what has arrived since you opened it.",
          ],
        },
        older: {
          title: "Load older activity",
          points: [
            "The feed loads 50 entries at a time and fetches more as you scroll towards the end.",
            "“Show More” at the bottom does the same from the keyboard.",
          ],
        },
        live: {
          title: "New activity arrives by itself",
          points: [
            "New entries appear while the page is open, and again when you return to the tab, without a reload.",
            "Entries you have already loaded stay in place.",
          ],
        },
        email: {
          title: "Get a digest by email",
          points: [
            "Every 15 minutes Franklin can email a summary of new assignments, @-mentions and comments on your issues.",
            "Turn it on or off with “Send me PM activity digest emails” in “User Settings”.",
          ],
        },
      },
    },
    settingsPersonal: {
      intro:
        "Your own profile and preferences. Set how your name and picture appear to others, which emails Franklin sends you and which chatbot actions run without asking first. The sidebar also leads to “Security”, “Connected apps” and “Invitations”, and the “Profile” menu at the bottom switches the app language.",
      items: {
        profile: {
          title: "Edit your details",
          points: [
            "Edit “First Name”, “Last Name”, “Email”, “Phone Number”, “Job Title” and “Company”.",
            "First and last name are required.",
            "Click “Save Changes”; “Cancel” puts back what was last saved.",
            "Changing “Email” also asks to change the email you sign in with.",
          ],
        },
        photo: {
          title: "Add a profile picture",
          points: [
            "Click your avatar (“Upload”) and pick a PNG, JPEG or WebP image of up to 2 MB.",
            "In “Adjust photo”, drag to position it, zoom with the slider and click “Save”.",
            "The picture is saved straight away; “Remove photo” takes it off again.",
          ],
        },
        avatarColour: {
          title: "Choose your avatar colour",
          points: [
            "Pick a swatch, or “Custom colour” to enter any hex colour.",
            "The colour fills your initials avatar whenever you have no picture.",
            "It is saved with “Save Changes”, not on its own.",
          ],
        },
        notifications: {
          title: "Choose your email notifications",
          points: [
            "“Send me PM activity digest emails”: every 15 minutes, a summary of new assignments, @-mentions and comments on your issues.",
            "“Send me message board notifications”: an email when someone posts on a project message board you can see.",
            "Untick to stop them, then click “Save Changes”.",
          ],
        },
        chatbot: {
          title: "Let Franklin act without asking",
          points: [
            "By default the chatbot shows a confirmation card before it changes your data.",
            "Under “Chatbot settings”, switch on “Update building data”, “Create issues”, “Update issues”, “Comment on issues”, “Add files to folders” or “Verify documents” to skip that card.",
            "Each switch saves straight away; switch it off to be asked again.",
            "On the card itself, ticking “Always allow this action” before approving switches it on for you. Some actions offer “Allow for the rest of this reply” instead, which lasts only until Franklin finishes its answer.",
            "Verifications made this way are still recorded in your name.",
          ],
        },
        language: {
          title: "Switch the language",
          points: [
            "Open “Profile” at the bottom of the sidebar.",
            "Choose “English” or “Eesti”; the tick marks the current one.",
          ],
        },
      },
    },
    settingsSecurity: {
      intro:
        "Keep your account secure. Here you change the password you sign in with. To sign out, use the “Profile” menu at the bottom of the sidebar.",
      items: {
        password: {
          title: "Change your password",
          points: [
            "Type the new password in “New Password” and again in “Confirm Password”.",
            "It must be at least 6 characters and both entries must match.",
            "Click “Update Password”; the fields clear once it is changed.",
          ],
        },
        signOut: {
          title: "Sign out",
          points: [
            "Open “Profile” at the bottom of the sidebar and choose “Logout”.",
            "You are taken back to the sign-in page.",
          ],
        },
      },
    },
    settingsInvitations: {
      intro:
        "Invitations to join an organisation that are waiting for your answer. Only invitations sent to your email that are still pending and have not expired are listed. Accepting one adds that organisation to the organisation menu at the bottom of the sidebar, alongside the ones you already belong to.",
      items: {
        accept: {
          title: "Accept an invitation",
          points: [
            "Each invitation shows the organisation, the email it was sent to, the role offered and when it expires.",
            "Click “Accept” to join with that role.",
          ],
        },
        decline: {
          title: "Decline an invitation",
          points: [
            "Click “Decline” and confirm in “Decline invitation?”.",
            "The invitation disappears from the list; an administrator can invite you again later.",
          ],
        },
        switchOrg: {
          title: "Open the organisation you joined",
          points: [
            "Open the organisation menu at the bottom of the sidebar; it lists every organisation you belong to.",
            "Pick one to switch to it and see its buildings.",
          ],
        },
        missing: {
          title: "If an invitation is missing",
          points: [
            "The list refreshes when you come back to this tab.",
            "Expired invitations are not shown; ask the organisation to resend it.",
            "An invitation sent to another email address will not appear here.",
          ],
        },
      },
    },
    settingsConnections: {
      intro:
        "Apps you have connected to Franklin with your account, such as an AI assistant. A connected app can only reach what your own Franklin permissions allow, in the organisation you chose when connecting. Changes and uploads it asks for open on their own approval pages, and you can cut any app off here at any time.",
      items: {
        review: {
          title: "See what each app can do",
          points: [
            "Each app shows its organisation and the permissions it was given, such as “Read information you can access” or “Create and update information”.",
            "“Connected” and “Last used” show when it was added and last used.",
            "Click “Load more” to see older connections.",
          ],
        },
        revoke: {
          title: "Revoke an app's access",
          points: [
            "Click “Revoke access” and confirm; the connection stops working immediately.",
            "The app then shows “Access revoked”.",
            "To use it again, connect and approve it again from the app.",
          ],
        },
        connect: {
          title: "Connect a new app",
          points: [
            "Start from the app; it sends you to “Connect to Franklin”.",
            "Choose the “Organization”, tick the “Permissions” to give and click “Allow access”.",
            "Only connect an app you recognise: the app supplies its own name.",
          ],
        },
        approveChange: {
          title: "Approve a change an app asks for",
          points: [
            "The app opens “Review requested change” with the proposed change and “All request details”.",
            "Click “Approve this change” or “Deny”.",
            "An approval covers that one request only and expires after 15 minutes; Franklin checks your permissions again before making it.",
          ],
        },
        appUpload: {
          title: "Upload a file an app requests",
          points: [
            "The app opens “Upload to Franklin” showing the requested file and its destination.",
            "Click “Choose file” and pick the file with that exact name and size.",
            "It is added as a new document; you can pause and resume by choosing the same file again.",
          ],
        },
      },
    },
    orgSettingsGeneral: {
      intro:
        "Your organisation's name and logo, as everyone in it sees them. The sidebar leads to the other organisation pages: “User Management”, “Usage” and, for owners and admins, “Billing”. You reach this page from the organisation menu at the bottom of the sidebar, under “Manage organisation”.",
      items: {
        rename: {
          title: "Rename the organisation",
          points: [
            "Edit “Organisation Name” and click “Save Changes”.",
            "“Cancel” puts the saved name back.",
            "The new name also appears in the organisation menu.",
          ],
        },
        logo: {
          title: "Set the logo",
          points: [
            "Click “Upload logo” (or “Replace logo”) and pick a PNG, JPEG or WebP image of up to 2 MB.",
            "Position and zoom it, then click “Save”; it applies straight away.",
            "“Remove logo” takes it off.",
            "The logo shows in the organisation menu and on the invitation emails you send.",
          ],
        },
        switchOrg: {
          title: "Switch organisation",
          points: [
            "Open the organisation menu at the bottom of the sidebar; a tick marks the current one.",
            "Pick another to switch to it and see its buildings.",
          ],
        },
      },
    },
    orgSettingsUsers: {
      intro:
        "Who belongs to your organisation and what they can do. “Organisation Invitations” lists invitations that are pending or expired, and “User Roles” lists every member with their role and status. An “Owner” can manage admins and members, an “Admin” can manage members only, and nobody can change an owner here.",
      items: {
        invite: {
          title: "Invite a user",
          points: [
            "Click “Invite User”, enter the “Email address” and choose a “Role”; “Member” is preselected.",
            "An owner can invite an “Admin” or a “Member”; an admin can invite members only.",
            "For a member, tick at least one project under “Projects this member can access”, and untick any folders they should not see.",
            "Click “Send invitation”; they get an email with a link.",
          ],
        },
        role: {
          title: "Change a role",
          points: [
            "Click a member's role pill (it has a ▾ when you may change it) and pick another role.",
            "“Admin” has full access to every project and to organisation settings; “Member” only to the projects they are given.",
            "Owners, your own role and roles above your tier cannot be changed.",
          ],
        },
        projectAccess: {
          title: "Set a member's project access",
          points: [
            "For a “Member”, click the folder icon (“Manage project access”).",
            "Tick projects on the left; untick a folder to hide it and the folders under it.",
            "Select a project and pick an “Access level” – “View only”, “Contributor” or “Full” – or set the switches under “What they can do” (shown as “Custom”).",
            "“Apply to all selected projects” copies the settings; click “Save”.",
          ],
        },
        remove: {
          title: "Remove a member",
          points: [
            "Click the bin icon on their row and confirm with “Remove”.",
            "They lose all access to this organisation; their account is not deleted.",
            "You cannot remove an owner, yourself or someone above your tier.",
          ],
        },
        invitations: {
          title: "Manage sent invitations",
          points: [
            "“Pending” invitations show who sent them and when they expire; use “Copy Link”, “Resend” or ⋯ → “Revoke”.",
            "“Expired” ones can be resent with a fresh link, or removed with “Delete”.",
            "“Clear all expired” removes every expired invitation at once.",
            "The list updates by itself when an invitation changes.",
          ],
        },
        members: {
          title: "Review the member list",
          points: [
            "“User Roles” shows each member's name, email, role and status (“Active” or “Inactive”).",
            "Click “Refresh” to reload it.",
          ],
        },
      },
    },
    orgSettingsUsage: {
      intro:
        "How much storage each building in the organisation uses against its plan's limit, broken down by project. This page is read-only. To get more space, the organisation owner changes the building's plan under “Billing”.",
      items: {
        storage: {
          title: "Check each building's storage",
          points: [
            "Each building shows the space used against its limit, a bar and the percentage used.",
            "A building with no limit shows “Unlimited”.",
          ],
        },
        limits: {
          title: "Spot buildings running out of space",
          points: [
            "“Storage nearly full” appears from 85% of the limit.",
            "“Storage full” means the limit is reached, and new uploads to that building are blocked.",
            "Free space by removing files or by moving the building to a plan with more storage.",
          ],
        },
        byProject: {
          title: "See which projects use the space",
          points: [
            "“Storage by project” lists each project's size and its share of the building's limit.",
            "Archived and hidden projects are not listed.",
            "A file shared between projects counts in each of them, but only once for the building.",
          ],
        },
        activationPending: {
          title: "Buildings awaiting activation",
          points: [
            "A building whose billing is not yet confirmed shows a notice instead of its usage.",
            "Complete any payment step under “Billing” → “Invoices”, then retry activation from the billing overview.",
          ],
        },
      },
    },
    billingOverview: {
      intro:
        "The buildings your organisation is billed for this period, the period's totals and the next renewal; invoices are issued from this list. Each building has its own plan, and removed buildings stay restorable for 30 days. Owners and admins can see this page, but only the organisation owner can add, change, remove or restore buildings. Company details and the payment method are on “Billing details”, and past invoices on “Invoices”.",
      items: {
        summary: {
          title: "Review the current period",
          points: [
            "“Current period” shows the dates and how many days remain.",
            "“Billed buildings” shows how many buildings are billed this period.",
            "“Monthly renewal” shows the date and total of the next renewal.",
          ],
        },
        addBuilding: {
          title: "Add a building",
          points: [
            "Click “Add building” under “Buildings this period”, fill in the name and address, and choose a “Plan”.",
            "“Free” needs no payment card; one Free building is allowed per organisation.",
            "A paid plan needs saved billing details and a payment method first; the dialog links to “Billing details”.",
            "Check the price shown and click “Agree and add building”.",
          ],
        },
        planLimits: {
          title: "Check a building's plan and limits",
          points: [
            "Each row shows the building's plan and monthly price, its service period and amount.",
            "The “i” icon beside the plan lists “Building storage”, “Non-archived projects”, “Chat questions per calendar month” and “PM issues”, with what is used.",
          ],
        },
        changePlan: {
          title: "Change a building's plan",
          points: [
            "Open the building's ⋮ menu, choose “Change plan”, pick a plan and click “Confirm plan”.",
            "Limits change once confirmed; the new price starts at the next renewal, with no charge or credit for the current cycle.",
            "While a change is pending, an “i” icon shows it and the plan cannot be changed again.",
          ],
        },
        removeBuilding: {
          title: "Remove a building",
          points: [
            "Open the building's ⋮ menu, choose “Remove” and confirm with “Remove building”.",
            "It stays fully usable until the end of the paid period and is then no longer charged.",
            "After that you can download its documents and restore it for 30 days; then it is deleted for good.",
            "Removal works even while the organisation is read-only.",
          ],
        },
        restoreBuilding: {
          title: "Restore a removed building",
          points: [
            "“Removed buildings” shows when each one stops being usable (“Usable until”) and is deleted (“Deleted on”).",
            "Click “Restore”, check the cost and click “Agree and restore building”; within the paid period there is no extra charge.",
            "Restoring is possible for 30 days, and not while the organisation is read-only or billing details or the payment method are missing.",
          ],
        },
        activation: {
          title: "Finish a pending activation",
          points: [
            "A building awaiting billing confirmation has no projects, chat questions, PM issues or uploads yet.",
            "In its ⋮ menu, “View invoice” opens “Invoices” to complete any payment step; then choose “Retry activation”.",
          ],
        },
        notices: {
          title: "Act on billing notices",
          points: [
            "Notices at the top flag an overdue invoice, a failed card payment, missing billing details or a missing payment method, each with a button to fix it.",
            "An invoice unpaid for 30 days makes the organisation read-only: everything stays visible and downloadable, but no buildings, projects or uploads can be added.",
            "A greyed-out button shows why it is unavailable when you point at it.",
          ],
        },
        transfer: {
          title: "Move a building to another organisation",
          points: [
            "When the owner or manager changes, a building and all its data can be transferred instead of removed.",
            "Write to support@frnkln.ai and the team arranges it.",
          ],
        },
      },
    },
    billingDetails: {
      intro:
        "The company details printed on every invoice and the payment method invoices are charged to. They must be in place before a paid building can be added or restored. Owners and admins can read them; only the organisation owner can change them, and admins see “Read-only billing details”.",
      items: {
        companyDetails: {
          title: "Update the invoice details",
          points: [
            "Fill in “Company name”, “Registry code”, “VAT number”, “Legal address” and “Invoice email”.",
            "“Registry code” is optional. The VAT number starts with its EU country prefix and must match that country's format.",
            "“Purchase order number” is optional and shown on every invoice.",
            "Click “Save details”; it is enabled once something has changed.",
          ],
        },
        paymentMethod: {
          title: "Set up the payment method",
          points: [
            "Under “Payment method”, choose “Card (automatic)” or “SEPA direct debit (automatic)”; each invoice is charged on the day it is issued.",
            "Click “Set up payment method” (or “Change”), or “Save and set up payment” if you also edited details.",
            "Card or bank details are entered on Stripe's secure page; you return with “Payment method saved.”",
            "Switching method does not carry the old one over; set the new one up too.",
          ],
        },
        continueAdding: {
          title: "Finish adding a building",
          points: [
            "If you came here while adding a building, its details are kept.",
            "Once the details and payment method are saved, you are taken back to the building form; “Continue adding building” does the same at any time.",
          ],
        },
        notices: {
          title: "Fix a failed payment",
          points: [
            "“Payment failed for invoice …” appears when the card could not be charged.",
            "Click “Update payment method” and set up a working method, or the organisation becomes read-only 30 days after the invoice date.",
          ],
        },
      },
    },
    billingInvoices: {
      intro:
        "Every invoice issued to your organisation, newest due date first. Each row shows the invoice number, how many buildings it covers, its period, due date, payment status and amount. Overdue invoices are also flagged in a notice above the list.",
      items: {
        download: {
          title: "Download an invoice",
          points: [
            "Click “Download” beside an invoice to save its PDF.",
            "If only Stripe's online invoice is available, it opens in a new tab instead.",
            "A greyed-out button means “The invoice PDF is not available yet.”",
          ],
        },
        status: {
          title: "Check what is paid",
          points: [
            "“Paid” invoices show “Paid on” with the date.",
            "“Due” is unpaid and within its term; “Overdue” is past its due date.",
            "“Payment failed” means an automatic charge was declined; other statuses are “Draft”, “Void” and “Written off”.",
            "Automatically charged invoices show “Charged on issue” instead of a due date.",
          ],
        },
        overdue: {
          title: "Settle an overdue invoice",
          points: [
            "An overdue invoice is flagged as “Invoice … is overdue” at the top of the billing pages.",
            "If it stays unpaid for 30 days, the organisation becomes read-only until it is settled.",
          ],
        },
      },
    },
    help: {
      intro:
        "The guide to every Franklin page, in one place under Settings. It starts with an overview of the platform, then lists each page's description and tasks, grouped by area. Search finds a task on any page.",
      items: {
        search: {
          title: "Search for a task",
          points: [
            "Type in the search box at the top; it searches the tasks of every page.",
            "Tasks containing your words appear straight away.",
            "Tasks related by meaning are listed below them.",
          ],
        },
        browse: {
          title: "Browse the guide by area",
          points: [
            "Start with the platform overview.",
            "Each page's description and tasks are grouped under its area.",
          ],
        },
        openPage: {
          title: "Go to a page",
          points: ["Click “Open …” on a page’s card to go straight there."],
        },
        pageInfo: {
          title: "Get help on any page",
          points: [
            "The “i” icon beside a page's title opens “About this page” with what you can do there.",
            "“Show me” points out the control a task describes.",
            "Its search box also finds tasks on every page.",
          ],
        },
      },
    },
  },
};
